import { z } from "zod";
import { MAX_ROOM_PARTICIPANTS } from "./room-limits.ts";

// Shared by the server and the room forms. Each error message is an error
// code that the UI translates under home.errors.*.
export const ROOM_NAME_MAX_LENGTH = 80;
// Creation, editing and joining share the same passcode limit. Older longer
// passcodes must be replaced before granting access to another account.
export const ROOM_PASSWORD_MAX_LENGTH = 6;

const name = z
  .string({ error: "invalid-room-name" })
  .trim()
  .min(1, { error: "invalid-room-name" })
  .max(ROOM_NAME_MAX_LENGTH, { error: "invalid-room-name" })
  // eslint-disable-next-line no-control-regex -- the C0 control range is intentionally rejected from room names
  .regex(/^[^\u0000-\u001f]*$/, { error: "invalid-room-name" });
const visibility = z.enum(["public", "private"], {
  error: "invalid-room-visibility",
});
const password = z
  .string({ error: "invalid-room-password" })
  .max(ROOM_PASSWORD_MAX_LENGTH, { error: "invalid-room-password" });
const maxParticipants = z
  .int({ error: "invalid-room-limit" })
  .min(1, { error: "invalid-room-limit" })
  .max(MAX_ROOM_PARTICIPANTS, { error: "invalid-room-limit" })
  .nullish()
  .transform((value) => value ?? null);

// Public rooms never require a passcode; only private rooms can have one.
const publicWithoutPassword = (
  room: { visibility?: "public" | "private"; password?: string },
  context: z.RefinementCtx,
) => {
  if (room.visibility === "public" && room.password)
    context.addIssue({ code: "custom", path: ["password"], message: "public-room-password" });
};

export const createRoomSchema = z
  .object(
    {
      visibility: visibility.default("public"),
      name,
      password: password.nullish().transform((value) => value ?? ""),
      maxParticipants,
    },
    { error: "invalid-room-name" },
  )
  .superRefine(publicWithoutPassword);

// An omitted password keeps the current one; an empty password removes it.
// Making a room public removes its passcode (see RoomStore.edit).
export const editRoomSchema = z
  .object(
    {
      name,
      maxParticipants,
      visibility: visibility.optional(),
      password: password.optional(),
    },
    { error: "invalid-room-name" },
  )
  .superRefine(publicWithoutPassword);

// Returns the parsed body, or throws the first issue's error code.
export function parseRoomInput<Schema extends z.ZodType>(
  schema: Schema,
  body: unknown,
  toError: (code: string) => Error = (code) => new Error(code),
) {
  const result = schema.safeParse(body);
  if (!result.success) throw toError(result.error.issues[0].message);
  return result.data;
}
